import React, { useState, useEffect } from 'react';
import './Dashboard.css';

function Dashboard({ balance, apiEndpoint, idToken, onNavigate, isManager, isSuperAdmin, onPayNow }) {
  const [schedule, setSchedule] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [expandedWeather, setExpandedWeather] = useState(false);
  const [expandedSchedule, setExpandedSchedule] = useState(false);
  const [expandedCalendar, setExpandedCalendar] = useState(false);
  const [metarSearch, setMetarSearch] = useState('EGSS');
  const [metarInput, setMetarInput] = useState('EGSS');
  const [metar, setMetar] = useState(null);
  const [taf, setTaf] = useState(null);
  const [metarDecoded, setMetarDecoded] = useState(null);
  const [tafDecoded, setTafDecoded] = useState(null);
  const [metarError, setMetarError] = useState(null);
  const [metarRaw, setMetarRaw] = useState(true);
  const [layout, setLayout] = useState(() => {
    const saved = localStorage.getItem('dashboardLayout');
    return saved ? JSON.parse(saved) : {
      balance: { row: 0, col: 0, span: 3 },
      schedule: { row: 1, col: 0 },
      calendar: { row: 1, col: 1 },
      weather: { row: 1, col: 2 }
    };
  });

  useEffect(() => {
    fetchSchedule();
    fetchMetarTaf('EGSS');
  }, []);

  const fetchMetarTaf = async (icao) => {
    const code = icao.trim().toUpperCase();
    setMetarError(null);
    setMetar(null);
    setTaf(null);
    setMetarDecoded(null);
    setTafDecoded(null);
    try {
      const res = await fetch(`${apiEndpoint}/metar?icao=${code}`, {
        headers: { Authorization: idToken }
      });
      const data = await res.json();
      if (!res.ok) {
        setMetarError(data.error || `No data found for ${code}`);
      } else {
        setMetar(data.metar);
        setTaf(data.taf || null);
        setMetarDecoded(data.metarDecoded || null);
        setTafDecoded(data.tafDecoded || null);
      }
    } catch {
      setMetarError('Failed to fetch weather data');
    }
  };

  const fetchSchedule = async () => {
    try {
      const response = await fetch(`${apiEndpoint}/schedule`, {
        headers: { Authorization: idToken }
      });
      const data = await response.json();
      setSchedule(data.schedule || []);
    } catch (error) {
      console.error('Error fetching schedule:', error);
    }
  };

  const getUpcoming = () => {
    return schedule.filter(s => new Date(s.date) >= new Date())
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 5);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getScheduleForDate = (date) => {
    const dateStr = formatDate(date);
    return schedule.find(s => s.date === dateStr);
  };

  const changeMonth = (offset) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset, 1));
  };

  const saveLayout = (newLayout) => {
    setLayout(newLayout);
    localStorage.setItem('dashboardLayout', JSON.stringify(newLayout));
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Welcome to Aircraft Tracking</h2>
        {isSuperAdmin && (
          <button onClick={() => setEditMode(!editMode)} className="edit-mode-btn">
            {editMode ? 'Save Layout' : 'Edit Layout'}
          </button>
        )}
      </div>

      <div className="dashboard-actions">
        <button onClick={() => onNavigate('entries')} className="action-btn">Add Flight Record</button>
        <button onClick={() => onNavigate('schedule')} className="action-btn">Flight Booking Schedule</button>
        {isManager && (
          <button onClick={() => onNavigate('manager')} className="action-btn manager">Manager Dashboard</button>
        )}
        {isSuperAdmin && (
          <button onClick={() => onNavigate('super-admin')} className="action-btn super-admin">Add Aircraft</button>
        )}
      </div>

      <div className="dashboard-widgets">
        <div className="widget balance-widget" style={{ gridRow: 1, gridColumn: '1 / -1' }}>
          <div className="widget-content balance-horizontal">
            {balance ? (
              <>
                <div className="balance-item-h">
                  <span className="balance-label">Balance:</span>
                  <span className="balance-value">£{balance.balance.toFixed(2)}</span>
                </div>
                <div className="balance-item-h">
                  <span className="balance-label">Flight Costs:</span>
                  <span className="balance-value">£{balance.totalEntries.toFixed(2)}</span>
                </div>
                <div className="balance-item-h">
                  <span className="balance-label">Paid:</span>
                  <span className="balance-value">£{balance.totalPayments.toFixed(2)}</span>
                </div>
                {balance.balance > 0 && (
                  <div className="balance-item-h">
                    <button className="pay-now-btn" onClick={onPayNow}>Pay Now</button>
                  </div>
                )}
              </>
            ) : (
              <p>Loading balance...</p>
            )}
          </div>
        </div>

        <div className="widget metar-widget" style={{ gridRow: 2, gridColumn: '1 / -1' }}>
          <div className="metar-bar">
            <div className="metar-controls">
              <form onSubmit={(e) => { e.preventDefault(); fetchMetarTaf(metarInput); }} className="metar-search">
                <input
                  value={metarInput}
                  onChange={(e) => setMetarInput(e.target.value.toUpperCase())}
                  placeholder="ICAO"
                  maxLength={4}
                  className="metar-input"
                />
                <button type="submit" className="metar-btn">Search</button>
              </form>
              <div className="metar-toggle">
                <span className={`toggle-label ${metarRaw ? 'active' : ''}`}>RAW</span>
                <div className="toggle-switch" onClick={() => setMetarRaw(!metarRaw)}>
                  <div className={`toggle-knob ${!metarRaw ? 'decoded' : ''}`} />
                </div>
                <span className={`toggle-label ${!metarRaw ? 'active' : ''}`}>Decoded</span>
              </div>
            </div>
            <div className="metar-results">
              {metarError && <span className="metar-error">{metarError}</span>}
              {metar && (
                <div className="metar-rows">
                  <div className="metar-row">
                    <span className="metar-label">METAR</span>
                    <span className="metar-text">{metarRaw ? metar : (metarDecoded || metar)}</span>
                  </div>
                  {taf && (
                    <div className="metar-row">
                      <span className="metar-label">TAF</span>
                      <span className="metar-text">{taf}</span>
                    </div>
                  )}
                </div>
              )}
              {!metar && !metarError && <span className="metar-loading">Loading...</span>}
            </div>
          </div>
        </div>

        <div className="widget" style={{ gridRow: 3, gridColumn: layout.schedule.col + 1 }}>
          <div className="widget-header">
            <h3>Upcoming Schedule</h3>
          </div>
          <div className="widget-content" onClick={() => setExpandedSchedule(true)} style={{ cursor: 'pointer' }}>
            {getUpcoming().length > 0 ? (
              <ul className="schedule-list">
                {getUpcoming().map((s, i) => (
                  <li key={i}>
                    <span className="schedule-date">{s.date}</span>
                    <span className="schedule-user">{s.userName}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No upcoming bookings</p>
            )}
          </div>
        </div>

        <div className="widget" style={{ gridRow: 3, gridColumn: layout.calendar.col + 1 }}>
          <div className="widget-header">
            <button onClick={(e) => { e.stopPropagation(); changeMonth(-1); }} className="month-nav">←</button>
            <h3>{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
            <button onClick={(e) => { e.stopPropagation(); changeMonth(1); }} className="month-nav">→</button>
          </div>
          <div className="widget-content calendar-widget" onClick={() => setExpandedCalendar(true)} style={{ cursor: 'pointer' }}>
            <div className="mini-calendar-weekdays">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                <div key={i} className="mini-weekday">{day}</div>
              ))}
            </div>
            <div className="mini-calendar-days">
              {getDaysInMonth(currentMonth).map((date, index) => {
                if (!date) return <div key={index} className="mini-day empty"></div>;
                
                const scheduleEntry = getScheduleForDate(date);
                const isToday = formatDate(date) === formatDate(new Date());
                
                return (
                  <div
                    key={index}
                    className={`mini-day ${scheduleEntry ? (scheduleEntry.type === 'out-of-service' ? 'out-of-service' : 'booked') : 'available'} ${isToday ? 'today' : ''}`}
                    title={scheduleEntry ? (scheduleEntry.type === 'out-of-service' ? `Out of Service${scheduleEntry.note ? ': ' + scheduleEntry.note : ''}` : scheduleEntry.userName) : 'Available'}
                  >
                    {date.getDate()}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="widget" style={{ gridRow: 3, gridColumn: layout.weather.col + 1 }}>
          <div className="widget-header">
            <h3>Weather - North Weald Airfield</h3>
          </div>
          <div className="widget-content" onClick={() => setExpandedWeather(true)} style={{ cursor: 'pointer' }}>
            <iframe
              src="https://embed.windy.com/embed2.html?lat=51.723&lon=0.155&detailLat=51.723&detailLon=0.155&width=650&height=450&zoom=11&level=surface&overlay=wind&product=ecmwf&menu=&message=&marker=&calendar=now&pressure=&type=map&location=coordinates&detail=&metricWind=default&metricTemp=default&radarRange=-1"
              width="100%"
              height="100%"
              frameBorder="0"
              sandbox="allow-scripts allow-same-origin"
            ></iframe>
          </div>
        </div>
      </div>

      {expandedWeather && (
        <div className="weather-overlay" onClick={() => setExpandedWeather(false)}>
          <div className="weather-overlay-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-overlay" onClick={() => setExpandedWeather(false)}>×</button>
            <iframe
              src="https://embed.windy.com/embed2.html?lat=51.723&lon=0.155&detailLat=51.723&detailLon=0.155&width=1200&height=800&zoom=11&level=surface&overlay=wind&product=ecmwf&menu=&message=&marker=&calendar=now&pressure=&type=map&location=coordinates&detail=&metricWind=default&metricTemp=default&radarRange=-1"
              width="100%"
              height="100%"
              frameBorder="0"
              sandbox="allow-scripts allow-same-origin"
            ></iframe>
          </div>
        </div>
      )}

      {expandedSchedule && (
        <div className="weather-overlay" onClick={() => setExpandedSchedule(false)}>
          <div className="weather-overlay-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-overlay" onClick={() => setExpandedSchedule(false)}>×</button>
            <div className="expanded-schedule-content">
              <h2>Upcoming Bookings</h2>
              {getUpcoming().length > 0 ? (
                <ul className="expanded-schedule-list">
                  {getUpcoming().map((s, i) => (
                      <li key={i}>
                        <span className="schedule-date">{s.date}</span>
                        <span className="schedule-user">{s.userName}</span>
                      </li>
                    ))}
                </ul>
              ) : (
                <p>No upcoming bookings</p>
              )}
            </div>
          </div>
        </div>
      )}

      {expandedCalendar && (
        <div className="weather-overlay" onClick={() => setExpandedCalendar(false)}>
          <div className="weather-overlay-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-overlay" onClick={() => setExpandedCalendar(false)}>×</button>
            <div className="expanded-calendar-content">
              <div className="expanded-calendar-header">
                <button onClick={() => changeMonth(-1)} className="month-nav-expanded">←</button>
                <h2>{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h2>
                <button onClick={() => changeMonth(1)} className="month-nav-expanded">→</button>
              </div>
              <div className="expanded-calendar-weekdays">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
                  <div key={i} className="expanded-weekday">{day}</div>
                ))}
              </div>
              <div className="expanded-calendar-days">
                {getDaysInMonth(currentMonth).map((date, index) => {
                  if (!date) return <div key={index} className="expanded-day empty"></div>;
                  
                  const scheduleEntry = getScheduleForDate(date);
                  const isToday = formatDate(date) === formatDate(new Date());
                  
                  return (
                    <div
                      key={index}
                      className={`expanded-day ${scheduleEntry ? (scheduleEntry.type === 'out-of-service' ? 'out-of-service' : 'booked') : 'available'} ${isToday ? 'today' : ''}`}
                      title={scheduleEntry ? (scheduleEntry.type === 'out-of-service' ? `Out of Service${scheduleEntry.note ? ': ' + scheduleEntry.note : ''}` : scheduleEntry.userName) : 'Available'}
                    >
                      <div className="expanded-day-number">{date.getDate()}</div>
                      {scheduleEntry && <div className="expanded-day-user">{scheduleEntry.type === 'out-of-service' ? '🔧' : scheduleEntry.userName}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
